import { TrashCan } from '@carbon/icons-react';
import {
  Button,
  InlineNotification,
  Modal,
  Select,
  SelectItem,
  SkeletonText,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Toggle,
} from '@carbon/react';
import { asProblem, problemMessage, RowAction } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { assignmentsApi, listAllPages, scopeApis, type Assignment } from './deliveryApi';

const ANY = '';

type AssignmentRow = Assignment & { version: number | string };

/** Names for each scope list, once, so a row can say "Quận 1 · Delivery" instead of ids. Every page is read. */
function useNames() {
  const lists = useQuery({
    queryKey: ['ho', 'delivery', 'scope-names'],
    queryFn: async () => {
      const [brands, stores, profiles, saleTypes] = await Promise.all(Object.values(scopeApis).map(listAllPages));
      return { brands: brands ?? [], stores: stores ?? [], profiles: profiles ?? [], saleTypes: saleTypes ?? [] };
    },
  });
  return lists.data;
}

/**
 * Where a delivery service is on offer (FR-DLV-02): rows by brand, store, POS profile and sale type; empty means all.
 * A service is offered only where an enabled row matches, and the most specific row decides.
 */
export function AssignmentsEditor({ serviceId }: { serviceId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const names = useNames();
  const key = ['ho', 'delivery', 'assignments', serviceId];
  const api = assignmentsApi(serviceId);
  const rows = useQuery({ queryKey: key, queryFn: () => listAllPages(api) });
  const [scope, setScope] = useState({ brandId: ANY, storeId: ANY, posProfileId: ANY, saleTypeId: ANY });
  const [deleting, setDeleting] = useState<AssignmentRow>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const add = useMutation({
    mutationFn: () =>
      api.create({
        brandId: scope.brandId || null,
        storeId: scope.storeId || null,
        posProfileId: scope.posProfileId || null,
        saleTypeId: scope.saleTypeId || null,
        isEnabled: true,
      }),
    onSuccess: async () => {
      setScope({ brandId: ANY, storeId: ANY, posProfileId: ANY, saleTypeId: ANY });
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (row: AssignmentRow) =>
      api.update(row, {
        brandId: row.brandId,
        storeId: row.storeId,
        posProfileId: row.posProfileId,
        saleTypeId: row.saleTypeId,
        isEnabled: !row.isEnabled,
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (row: AssignmentRow) => api.remove(row),
    onSuccess: async () => {
      setDeleting(undefined);
      await refresh();
    },
  });
  const busy = add.isPending || toggle.isPending || remove.isPending;

  // While the names load, or when a record is gone, the row says so instead of showing a placeholder.
  const nameOf = (list: { id: string; name: string }[] | undefined, id: string) =>
    list?.find((x) => x.id === id)?.name ?? t('delivery.services.scope.unknown', { id: id.slice(0, 8) });
  const scopeText = (row: Assignment) => {
    const parts = [
      row.posProfileId && `${t('delivery.services.scope.profile')}: ${nameOf(names?.profiles, row.posProfileId)}`,
      row.storeId && `${t('delivery.services.scope.store')}: ${nameOf(names?.stores, row.storeId)}`,
      row.brandId && `${t('delivery.services.scope.brand')}: ${nameOf(names?.brands, row.brandId)}`,
      row.saleTypeId && `${t('delivery.services.scope.saleType')}: ${nameOf(names?.saleTypes, row.saleTypeId)}`,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : t('delivery.services.scope.everywhere');
  };

  const pick = (
    id: string,
    label: string,
    list: { id: string; name: string }[] | undefined,
    field: keyof typeof scope,
  ) => (
    <Select
      id={id}
      labelText={label}
      value={scope[field]}
      disabled={add.isPending}
      onChange={(e) => setScope((s) => ({ ...s, [field]: e.target.value }))}
    >
      <SelectItem value={ANY} text={t('delivery.services.scope.any')} />
      {(list ?? []).map((x) => (
        <SelectItem key={x.id} value={x.id} text={x.name} />
      ))}
    </Select>
  );

  return (
    <section className="pos-delivery__assignments" aria-label={t('delivery.services.assignments.title')}>
      <h3 className="pos-delivery__subheading">{t('delivery.services.assignments.title')}</h3>
      <p className="pos-delivery__hint">{t('delivery.services.assignments.hint')}</p>
      {rows.isLoading && <SkeletonText paragraph lineCount={2} />}
      {rows.error && (
        <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(rows.error))} />
      )}
      {(rows.data ?? []).length > 0 && (
        <StructuredListWrapper isCondensed aria-label={t('delivery.services.assignments.title')}>
          <StructuredListBody>
            {(rows.data ?? []).map((row) => {
              const record = row as AssignmentRow;
              return (
                <StructuredListRow key={row.id}>
                  <StructuredListCell>{scopeText(row)}</StructuredListCell>
                  <StructuredListCell>
                    <Toggle
                      id={`assignment-${row.id}`}
                      hideLabel
                      labelText={scopeText(row)}
                      labelA={t('delivery.services.assignments.off')}
                      labelB={t('delivery.services.assignments.on')}
                      toggled={row.isEnabled}
                      disabled={busy}
                      onToggle={() => toggle.mutate(record)}
                    />
                  </StructuredListCell>
                  <StructuredListCell>
                    <RowAction
                      action={t('common.delete')}
                      record={scopeText(row)}
                      icon={TrashCan}
                      onClick={() => {
                        remove.reset();
                        setDeleting(record);
                      }}
                    />
                  </StructuredListCell>
                </StructuredListRow>
              );
            })}
          </StructuredListBody>
        </StructuredListWrapper>
      )}
      {rows.data?.length === 0 && <p className="pos-delivery__hint">{t('delivery.services.assignments.none')}</p>}
      <div className="pos-delivery__scope">
        {pick('scope-brand', t('delivery.services.scope.brand'), names?.brands, 'brandId')}
        {pick('scope-store', t('delivery.services.scope.store'), names?.stores, 'storeId')}
        {pick('scope-profile', t('delivery.services.scope.profile'), names?.profiles, 'posProfileId')}
        {pick('scope-sale-type', t('delivery.services.scope.saleType'), names?.saleTypes, 'saleTypeId')}
        <Button kind="tertiary" disabled={busy} onClick={() => add.mutate()}>
          {t('delivery.services.assignments.add')}
        </Button>
      </div>
      {(add.error || toggle.error) && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(add.error ?? toggle.error))}
        />
      )}
      {deleting && (
        <Modal
          open
          danger
          preventCloseOnClickOutside
          modalHeading={t('delivery.services.assignments.deleteTitle')}
          closeButtonLabel={t('carbon.close')}
          primaryButtonText={t('common.delete')}
          secondaryButtonText={t('common.cancel')}
          primaryButtonDisabled={remove.isPending}
          onRequestClose={() => setDeleting(undefined)}
          onRequestSubmit={() => remove.mutate(deleting)}
        >
          <p>{t('delivery.services.assignments.deleteBody', { scope: scopeText(deleting) })}</p>
          {remove.error && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(remove.error))}
            />
          )}
        </Modal>
      )}
    </section>
  );
}
